/**
 * Centralized Permission Registry
 *
 * This file defines all application permissions in a centralized location.
 * Each permission follows the format: "resource:action"
 */

// Core permissions for main features
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: "dashboard:view",

  // Wizards (Example Entity)
  WIZARDS_VIEW: "wizards:view",
  WIZARDS_CREATE: "wizards:create",
  WIZARDS_EDIT: "wizards:edit",
  WIZARDS_DELETE: "wizards:delete",
  WIZARDS_EXPORT: "wizards:export",

  // Master Data - Users
  USERS_VIEW: "users:view",
  USERS_CREATE: "users:create",
  USERS_EDIT: "users:edit",
  USERS_DELETE: "users:delete",

  // Master Data - Companies
  COMPANIES_VIEW: "companies:view",
  COMPANIES_CREATE: "companies:create",
  COMPANIES_EDIT: "companies:edit",
  COMPANIES_DELETE: "companies:delete",

  // Master Data - Customers
  CUSTOMERS_VIEW: "customers:view",
  CUSTOMERS_CREATE: "customers:create",
  CUSTOMERS_EDIT: "customers:edit",
  CUSTOMERS_DELETE: "customers:delete",

  // Master Data - Connectors
  CONNECTORS_VIEW: "connectors:view",
  CONNECTORS_CREATE: "connectors:create",
  CONNECTORS_EDIT: "connectors:edit",
  CONNECTORS_DELETE: "connectors:delete",

  // Master Data - Webhook Queue
  WEBHOOK_QUEUE_VIEW: "webhook_queue:view",
  WEBHOOK_QUEUE_EDIT: "webhook_queue:edit",
  WEBHOOK_QUEUE_DELETE: "webhook_queue:delete",

  // Orders Failed
  ORDERS_FAILED_VIEW: "orders_failed:view",
  ORDERS_FAILED_EDIT: "orders_failed:edit",

  // Notifications
  NOTIFICATIONS_VIEW: "notifications:view",
  NOTIFICATIONS_CREATE: "notifications:create",
  NOTIFICATIONS_EDIT: "notifications:edit",
  NOTIFICATIONS_DELETE: "notifications:delete",

  // Notifications Queue
  NOTIFICATIONS_QUEUE_VIEW: "notifications_queue:view",
  NOTIFICATIONS_QUEUE_EDIT: "notifications_queue:edit",
  NOTIFICATIONS_QUEUE_DELETE: "notifications_queue:delete",

  // Settings - General
  SETTINGS_GENERAL_VIEW: "settings_general:view",
  SETTINGS_GENERAL_EDIT: "settings_general:edit",

  // Admin privileges
  ADMIN_PANEL: "admin:panel",
  ADMIN_USER_MANAGEMENT: "admin:user_management",
  ADMIN_SYSTEM_CONFIG: "admin:system_config",
} as const;

/**
 * Type-safe permission type derived from the registry
 */
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * User roles from backend Symfony roles
 * @see wicssendapi/src/Enum/Role.php
 */
export const USER_ROLES = {
  ROLE_API: "ROLE_API",
  ROLE_SUPER_ADMIN: "ROLE_SUPER_ADMIN",
  ROLE_COMPANY_OWNER: "ROLE_COMPANY_OWNER",
  ROLE_COMPANY_INVITEE: "ROLE_COMPANY_INVITEE",
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

/**
 * Default permissions by role
 * This provides a mapping of what permissions each role typically has
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [USER_ROLES.ROLE_SUPER_ADMIN]: Object.values(PERMISSIONS),

  [USER_ROLES.ROLE_COMPANY_OWNER]: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.WIZARDS_VIEW,
    PERMISSIONS.WIZARDS_CREATE,
    PERMISSIONS.WIZARDS_EDIT,
    PERMISSIONS.WIZARDS_DELETE,
    PERMISSIONS.WIZARDS_EXPORT,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.COMPANIES_VIEW,
    PERMISSIONS.COMPANIES_EDIT,
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_EDIT,
    PERMISSIONS.CONNECTORS_VIEW,
    PERMISSIONS.CONNECTORS_CREATE,
    PERMISSIONS.CONNECTORS_EDIT,
    PERMISSIONS.WEBHOOK_QUEUE_VIEW,
    PERMISSIONS.ORDERS_FAILED_VIEW,
    PERMISSIONS.ORDERS_FAILED_EDIT,
    PERMISSIONS.NOTIFICATIONS_VIEW,
    PERMISSIONS.NOTIFICATIONS_CREATE,
    PERMISSIONS.NOTIFICATIONS_EDIT,
    PERMISSIONS.NOTIFICATIONS_QUEUE_VIEW,
    PERMISSIONS.SETTINGS_GENERAL_VIEW,
    PERMISSIONS.SETTINGS_GENERAL_EDIT,
  ],

  [USER_ROLES.ROLE_COMPANY_INVITEE]: [PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.WIZARDS_VIEW],

  [USER_ROLES.ROLE_API]: [PERMISSIONS.WIZARDS_VIEW, PERMISSIONS.WIZARDS_CREATE],
};

/**
 * Get all permissions for an array of roles (users can have multiple roles)
 */
export const getPermissionsForRoles = (roles: string[]): Permission[] => {
  const permissionSet = new Set<Permission>();

  for (const role of roles) {
    const rolePerms = DEFAULT_ROLE_PERMISSIONS[role as UserRole];
    if (rolePerms) {
      rolePerms.forEach((perm) => permissionSet.add(perm));
    }
  }

  return Array.from(permissionSet);
};

/**
 * Helper function to get all permissions for a given role
 * @deprecated Use getPermissionsForRoles for multiple roles
 */
export const getPermissionsForRole = (role: UserRole): Permission[] => {
  return DEFAULT_ROLE_PERMISSIONS[role] || [];
};

/**
 * Helper function to check if a role has a specific permission by default
 */
export const roleHasPermission = (role: UserRole, permission: Permission): boolean => {
  return DEFAULT_ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
};
